import { GrowthBook } from '@growthbook/growthbook';
import { BrowserCookieStickyBucketService } from '@growthbook/growthbook';
import Cookies from 'js-cookie';
import * as features from '@/scripts/store/features';
import { head } from 'underscore';

const growth_book = new GrowthBook({
  apiHost: 'https://cdn.growthbook.io',
  clientKey: 'sdk-bFMPGDFC9sMwhjsE',
  enableDevMode: process.env.NODE_ENV === 'development',
  subscribeToChanges: true,
  stickyBucketService: new BrowserCookieStickyBucketService({
    jsCookie: Cookies,
  }),
  trackingCallback: (experiment, result) => {
    console.log('viewed experiment', experiment.key, result.key);
    window.heap?.track?.('Viewed Experiment', {
      experimentId: experiment.key,
      variationId: result.key,
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'viewed_experiment',
      experimentId: experiment.key,
      variationId: result.key,
    });
  },
});

window.gb = growth_book; // Expose for debugging
window.growth_book = growth_book; // Expose for debugging

//===========================
// Collect user attributes
//===========================

//should match tailwind.config.js
const screens = {
  sm: '700px',
  md: '1000px',
  lg: '1150px',
  xl: '1400px',
  '2xl': '1600px',
};

const screenSize = Object.keys(screens)
  .reverse()
  .find(key => {
    return window.innerWidth > parseInt(screens[key]);
  });

const getHeapIdFromStorage = function () {
  try {
    const appId = window.heap?.appid;
    const key = appId ? `_hp5_meta.${appId}` : Object.keys(localStorage).find(k => k.startsWith('_hp5_meta.'));
    if (key) {
      const meta = JSON.parse(localStorage.getItem(key));
      return meta?.value?.userId || null;
    }
  } catch (e) {
    //do nothing
  }
  return null;
};

const waitForHeapId = function () {
  const storedId = getHeapIdFromStorage();
  if (storedId) {
    setGbAttributes(storedId);
    return Promise.resolve();
  }

  // Heap is gated by consent, don't wait on it if it was skipped
  if (window.heapSkipped) {
    console.log('[GrowthBook] heap skipped, proceeding without Heap ID');
    setGbAttributes(null);
    return Promise.resolve();
  }

  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      console.warn('heapReady timeout — proceeding without Heap ID');
      setGbAttributes(getHeapIdFromStorage());
      resolve();
    }, 5000);

    window.addEventListener('heapSkipped', function () {
      clearTimeout(timeout);
      console.log('[GrowthBook] heap skipped, proceeding without Heap ID');
      setGbAttributes(null);
      resolve();
    }, { once: true });

    window.addEventListener('heapReady', function () {
      clearTimeout(timeout);
      const heapId = getHeapIdFromStorage();
      if (heapId) {
        setGbAttributes(heapId);
        resolve();
      } else {
        // heapReady can fire before userId is written — wait briefly for new visitors
        setTimeout(() => {
          setGbAttributes(window.heap?.getUserId() || getHeapIdFromStorage());
          resolve();
        }, 500);
      }
    });
  });
};

const setGbAttributes = function (id) {
  const attributes = {
    id: id,
    heapId: id || 'unknown',
    email: window?.userAttributes?.email,
    customerId: window?.userAttributes?.customerId,
    totalSpent: window?.userAttributes?.totalSpent,
    ordersCount: window?.userAttributes?.ordersCount,
    tags: window?.userAttributes?.tags,
    country: Shopify.country,
    locale: Shopify?.locale,
    currency: Shopify?.currency?.active,
    url: window.location.href,
    userAgent: navigator.userAgent,
    screenWidth: window.innerWidth,
    screenSize: screenSize,
    themeId: Shopify.theme.id,
    themeName: Shopify.theme.name,
    themeRole: Shopify.theme.role,
    params: paramsToObject(new URL(document.location).searchParams),
  };

  growth_book.setAttributes(attributes);

  try {
    window.heap?.track?.('growthbook:attributes', attributes);
  } catch (e) {
    console.log('heap not ready?');
  }
};

//===========================
// setup growthbook
//===========================

function paramsToObject(entries) {
  const result = {};
  for (const [key, value] of entries) {
    // each 'entry' is a [key, value] tupple
    result[key] = value;
  }
  return result;
}

export const setupGrowthbook = async function () {
  window.dispatchEvent(new Event('growthbook:setup'));

  try {
    window.heap?.track?.('growthbook:setup');
  } catch (e) {
    console.log('heap not ready?');
  }

  await waitForHeapId();

  // Wait for features to be available
  console.log('[GrowthBook] init start', { attributes: growth_book.getAttributes(), devMode: process.env.NODE_ENV === 'development' });
  const initResult = await growth_book.init({ timeout: 1000 });
  console.log('[GrowthBook] init done', {
    status: initResult?.status,
    source: initResult?.source,
    error: initResult?.error,
    ready: growth_book.ready,
    featureCount: Object.keys(growth_book.getFeatures() || {}).length,
  });
  console.log('[GrowthBook] code features', Object.keys(features).map(key => features[key].key));

  for (const key in features) {
    const feature = features[key];
    const value = growth_book.getFeatureValue(feature.key);
    console.log(`[GrowthBook] feature "${feature.key}" value:`, value, typeof value != 'undefined' ? '(init)' : '(skipped, undefined)');
    if (typeof value != 'undefined') {
      feature.init(value);
    }
  }

  growth_book.setRenderer(() => {
    console.log('[GrowthBook] renderer fired, re-running features');
    for (const key in features) {
      const feature = features[key];
      const value = growth_book.getFeatureValue(feature.key);
      console.log(`[GrowthBook] (render) feature "${feature.key}" value:`, value);
      if (typeof value != 'undefined') {
        feature.init(value);
      }
    }
  });

  //publish event that growthbook is ready
  window.dispatchEvent(new Event('growthbook:ready'));

  try {
    window.heap?.track?.('growthbook:ready');
  } catch (e) {
    console.log('heap not ready?');
  }
};
